import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Eyebrow, PageHero } from "@/components/shared";
import { MapPin, Mail } from "lucide-react";
import { ADDRESS, EMAIL } from "@/data/content";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", organisation: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        organisation: form.organisation || null,
        subject: form.subject,
        message: form.message,
      });
      toast.success(t("contact.toast.success"));
      setForm({ name: "", email: "", phone: "", organisation: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t("contact.toast.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        testId="contact-hero"
        eyebrow={t("contact.heroEyebrow")}
        title={t("contact.heroTitle")}
        intro={t("contact.heroIntro")}
      />

      <section className="bg-ivory" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <Eyebrow>{t("contact.findEyebrow")}</Eyebrow>
            <h2 className="text-[28px] sm:text-3xl font-semibold text-charcoal">{t("contact.findHeading")}</h2>
            <div className="mt-8 space-y-6">
              <div className="flex gap-4 items-start">
                <MapPin size={24} strokeWidth={1.5} className="text-saffron shrink-0 mt-1" aria-hidden="true" />
                <div>
                  <p className="font-sans font-bold text-sm uppercase tracking-[0.14em] text-earth">{t("contact.addressLabel")}</p>
                  <p className="mt-1 text-charcoal/85 leading-relaxed" data-testid="contact-address">{ADDRESS}</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <Mail size={24} strokeWidth={1.5} className="text-saffron shrink-0 mt-1" aria-hidden="true" />
                <div>
                  <p className="font-sans font-bold text-sm uppercase tracking-[0.14em] text-earth">{t("contact.emailLabel")}</p>
                  <a href={`mailto:${EMAIL}`} className="mt-1 inline-block text-saffron font-semibold no-underline hover:underline" data-testid="contact-email-link">
                    {EMAIL}
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-10 card-ivory p-6">
              <p className="font-serif italic text-lg text-charcoal/90 leading-relaxed">{t("contact.quote")}</p>
              <p className="mt-3 font-sans text-[13px] tracking-[0.16em] uppercase text-earth">{t("contact.quoteAttr")}</p>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={100}>
            <h2 className="text-[28px] sm:text-3xl font-semibold text-charcoal mb-8">{t("contact.formHeading")}</h2>
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="ct-name" className="block font-sans font-semibold text-sm text-charcoal mb-2">{t("contact.form.name")}</label>
                  <input id="ct-name" name="name" required value={form.name} onChange={update} className="input-field" placeholder={t("contact.form.namePh")} data-testid="contact-name-input" />
                </div>
                <div>
                  <label htmlFor="ct-email" className="block font-sans font-semibold text-sm text-charcoal mb-2">{t("contact.form.email")}</label>
                  <input id="ct-email" name="email" type="email" required value={form.email} onChange={update} className="input-field" placeholder={t("contact.form.emailPh")} data-testid="contact-email-input" />
                </div>
                <div>
                  <label htmlFor="ct-phone" className="block font-sans font-semibold text-sm text-charcoal mb-2">{t("contact.form.phone")}</label>
                  <input id="ct-phone" name="phone" type="tel" value={form.phone} onChange={update} className="input-field" placeholder={t("contact.form.phonePh")} data-testid="contact-phone-input" />
                </div>
                <div>
                  <label htmlFor="ct-org" className="block font-sans font-semibold text-sm text-charcoal mb-2">{t("contact.form.org")}</label>
                  <input id="ct-org" name="organisation" value={form.organisation} onChange={update} className="input-field" placeholder={t("contact.form.orgPh")} data-testid="contact-organisation-input" />
                </div>
              </div>
              <div>
                <label htmlFor="ct-subject" className="block font-sans font-semibold text-sm text-charcoal mb-2">{t("contact.form.subject")}</label>
                <input id="ct-subject" name="subject" required value={form.subject} onChange={update} className="input-field" placeholder={t("contact.form.subjectPh")} data-testid="contact-subject-input" />
              </div>
              <div>
                <label htmlFor="ct-message" className="block font-sans font-semibold text-sm text-charcoal mb-2">{t("contact.form.message")}</label>
                <textarea id="ct-message" name="message" required rows={6} value={form.message} onChange={update} className="input-field" placeholder={t("contact.form.messagePh")} data-testid="contact-message-input" />
              </div>
              <button type="submit" disabled={sending} className="btn btn-primary disabled:opacity-60" data-testid="contact-submit-button">
                {sending ? t("contact.form.sending") : t("contact.form.submit")}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
